/**
 * The handful of things every screen needs.
 *
 * Kept small on purpose: a shared hooks file grows into a junk drawer, so anything that
 * belongs to one screen stays in that screen.
 */
import { useCallback, useState } from "react";
import { formatMonthName, type Period } from "@onecpa/shared";
import { useSession } from "./session.js";

/**
 * The period selector shared by the matrix, billing and the analytics screens.
 *
 * Defaults to the current month in Asia/Jerusalem rather than UTC: on the first of the
 * month, a UTC-derived default would show the previous month to anyone opening the app
 * before 03:00 local time.
 */
export function usePeriod(): {
  period: Period;
  label: string;
  next(): void;
  previous(): void;
  reset(): void;
  /** Jump to a specific month, for when an action lands somewhere else. */
  goTo(target: Period): void;
  isCurrent: boolean;
} {
  const current = jerusalemPeriod();
  const [period, setPeriod] = useState<Period>(current);

  const shift = useCallback((offset: number) => {
    setPeriod((value) => {
      const zeroBased = value.year * 12 + (value.month - 1) + offset;
      return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
    });
  }, []);

  return {
    period,
    label: `${formatMonthName(period)} ${period.year}`,
    next: () => shift(1),
    previous: () => shift(-1),
    reset: () => setPeriod(current),
    goTo: setPeriod,
    isCurrent: period.year === current.year && period.month === current.month,
  };
}

function jerusalemPeriod(): Period {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
  }).format(new Date());
  const [year, month] = parts.split("-").map(Number) as [number, number];
  return { year, month };
}

/**
 * Refuses to render a screen the caller's role may not see.
 *
 * Returns a message rather than redirecting: a bookkeeper who follows a stale link to
 * profitability should learn that the screen exists and is not theirs, not be bounced
 * somewhere else without explanation.
 */
export function useRoleGate(allowed: string[]): { allowed: boolean; role: string } {
  const { role } = useSession();
  return { allowed: allowed.includes(role), role };
}

/** Money crosses the wire as a decimal string. Convert once, at the render boundary. */
export function agorot(value: string | null | undefined): number {
  return value === null || value === undefined ? 0 : Math.round(Number(value) * 100);
}

export function minutesToLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} דק׳`;
  return rest === 0 ? `${hours} ש׳` : `${hours}:${String(rest).padStart(2, "0")}`;
}
